import { useMemo, useState } from "react";
// Imported from the module rather than the helpers barrel: this is a leaf component, and the barrel
// reaches transitively into the rest of the app, which drags the whole tree in behind it.
import {
    azimuthErrorDegrees,
    cellKey,
    finiteExtent,
    FovSweepGrid,
    FovSweepPoint,
} from "../../helpers/fovSweepHelpers";

/**
 * Draws one field-of-view sweep metric as a grid, one rectangle per commanded gimbal angle.
 *
 * The sweep is the only part of a calibration report that is genuinely two-dimensional, and the numbers
 * it rolls up to — one dead-cell count, one angular RMS — collapse that away. Whether 27 dead cells are
 * scattered across the middle of the field or stacked in the outermost azimuth columns is the difference
 * between a nulled RX channel and the beam simply running out at the edge of a sweep deliberately taken
 * wider than the unit ships with, and no scalar in the report can tell those apart. Hence the picture.
 */

type Scale = {
    /** Maps a measured value onto a fill. */
    fill: (value: number) => string;
    min: number;
    max: number;
    /** Text for the colour bar ends. */
    minLabel: string;
    maxLabel: string;
};

const GRID_UNDETECTED_FILL = "#d1d5db"; // gray-300
const GRID_UNDETECTED_STROKE = "#9ca3af"; // gray-400
const DEAD_STROKE = "#dc2626"; // red-600
const AMBIGUOUS_STROKE = "#7c3aed"; // violet-600 — a different fault, so a different colour

/** Linear interpolation between two "#rrggbb" colours. */
const mix = (from: string, to: string, t: number) => {
    const clamped = Math.min(1, Math.max(0, t));
    const parse = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    const [r1, g1, b1] = parse(from);
    const [r2, g2, b2] = parse(to);
    const channel = (a: number, b: number) => Math.round(a + (b - a) * clamped);
    return `rgb(${channel(r1, r2)}, ${channel(g1, g2)}, ${channel(b1, b2)})`;
};

/**
 * Three-stop ramp. Two stops is not enough for either metric here: SNR needs a dark low end that stays
 * distinguishable from the grey used for undetected cells, and the error map needs a mid tone so a 20°
 * cell does not read as though it were as bad as a 90° one.
 */
const ramp = (stops: [string, string, string]) => (t: number) =>
    t <= 0.5 ? mix(stops[0], stops[1], t * 2) : mix(stops[1], stops[2], (t - 0.5) * 2);

// Blue → yellow, dark end for weak returns. Reads as "how much signal came back".
const snrRamp = ramp(["#1e3a8a", "#38bdf8", "#fde047"]);

// Green → amber → red. Reads as "how wrong the reported angle was", which is a severity, not a quantity.
const errorRamp = ramp(["#15803d", "#facc15", "#b91c1c"]);

const format = (value: number, digits = 1) => (Number.isFinite(value) ? value.toFixed(digits) : "—");

/**
 * Where <paramref name="target"/> sits along an evenly spaced axis, as a fractional index. Undefined when
 * the value is not finite or falls outside the swept extent, so the caller can simply not draw it.
 */
const axisPosition = (values: number[], target: number): number | undefined => {
    if (!Number.isFinite(target) || values.length < 2) return undefined;

    const first = values[0];
    const step = values[1] - first;
    if (step === 0) return undefined;

    const index = (target - first) / step;
    return index >= 0 && index <= values.length - 1 ? index : undefined;
};

type FovSweepHeatmapProps = {
    grid: FovSweepGrid;
    title: string;
    subtitle: string;
    unit: string;
    /** Value drawn for a cell; NaN renders as the "no measurement" fill. */
    value: (point: FovSweepPoint) => number;
    ramp: (t: number) => string;
    /**
     * Clamps the top of the colour scale. The azimuth error map needs it: a handful of mirror-image
     * cells reporting 90° of error would otherwise compress every real 5–15° difference into one shade.
     */
    maxValue?: number;
};

const Heatmap: React.FC<FovSweepHeatmapProps> = ({
    grid,
    title,
    subtitle,
    unit,
    value,
    ramp: colourRamp,
    maxValue,
}) => {
    const [hovered, setHovered] = useState<FovSweepPoint>();

    const scale = useMemo<Scale>(() => {
        const extent = finiteExtent(grid.points, value) ?? { min: 0, max: 1 };
        const min = extent.min;
        const max = maxValue !== undefined ? Math.min(extent.max, maxValue) : extent.max;
        // A flat field would divide by zero; give it a nominal span so every cell lands mid-ramp.
        const span = max - min || 1;

        return {
            fill: (v: number) => colourRamp((v - min) / span),
            min,
            max,
            minLabel: `${format(min)} ${unit}`,
            maxLabel: `${maxValue !== undefined && extent.max > maxValue ? "≥" : ""}${format(max)} ${unit}`,
        };
    }, [grid.points, value, colourRamp, unit, maxValue]);

    const cell = 18;
    const left = 44;
    const top = 22;
    const width = left + grid.azimuths.length * cell + 8;
    const height = top + grid.elevations.length * cell + 26;

    // Only every third column gets a label, or the axis is unreadable at 5° steps.
    const labelledAzimuths = new Set(grid.azimuths.filter((_, i) => i % 3 === 0));

    // Column boundaries either side of the graded window, as column indices. Omitted when the window
    // covers the whole sweep, where a line on each outer edge would say nothing.
    const gradedLimit = grid.evaluationAzimuthLimitDegrees;
    const insideGrade = gradedLimit === undefined ? [] : grid.azimuths.filter((a) => Math.abs(a) <= gradedLimit);
    // Fractional grid position of the reported beam centre, so the marker can sit between cells.
    const boresight = (() => {
        const column = axisPosition(grid.azimuths, grid.boresightAzimuthDegrees);
        const row = axisPosition(grid.elevations, grid.boresightElevationDegrees);
        return column === undefined || row === undefined ? undefined : { column, row };
    })();

    const gradedEdges =
        gradedLimit === undefined || insideGrade.length === 0 || insideGrade.length === grid.azimuths.length
            ? []
            : [grid.azimuths.indexOf(insideGrade[0]), grid.azimuths.indexOf(insideGrade[insideGrade.length - 1]) + 1];

    return (
        <div className="min-w-0">
            <div className="mb-1">
                <div className="text-sm font-medium">{title}</div>
                <div className="text-[11px] text-gray-500">{subtitle}</div>
            </div>

            <div className="overflow-x-auto">
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    width={width}
                    height={height}
                    role="img"
                    aria-label={`${title} across the swept field of view`}
                    className="max-w-full"
                >
                    {grid.elevations.map((elevation, row) => (
                        <text
                            key={`el-${elevation}`}
                            x={left - 5}
                            y={top + row * cell + cell / 2 + 3}
                            textAnchor="end"
                            className="fill-gray-500"
                            fontSize={8}
                        >
                            {elevation}
                        </text>
                    ))}

                    {grid.azimuths.map((azimuth, column) =>
                        labelledAzimuths.has(azimuth) ? (
                            <text
                                key={`az-${azimuth}`}
                                x={left + column * cell + cell / 2}
                                y={height - 14}
                                textAnchor="middle"
                                className="fill-gray-500"
                                fontSize={8}
                            >
                                {azimuth}
                            </text>
                        ) : null,
                    )}

                    {grid.elevations.map((elevation, row) =>
                        grid.azimuths.map((azimuth, column) => {
                            const key = cellKey(azimuth, elevation);
                            const point = grid.byCell.get(key);
                            const measured = point ? value(point) : NaN;
                            const isDead = grid.deadCells.has(key);
                            const isAmbiguous = grid.ambiguousCells.has(key);
                            const hasValue = Number.isFinite(measured);

                            return (
                                <rect
                                    key={key}
                                    x={left + column * cell}
                                    y={top + row * cell}
                                    width={cell - 1}
                                    height={cell - 1}
                                    fill={hasValue ? scale.fill(measured) : GRID_UNDETECTED_FILL}
                                    // Flagged cells get an outline rather than their own colour so both the
                                    // device's verdict and the underlying measurement stay visible at once.
                                    stroke={
                                        isDead
                                            ? DEAD_STROKE
                                            : isAmbiguous
                                              ? AMBIGUOUS_STROKE
                                              : hasValue
                                                ? "none"
                                                : GRID_UNDETECTED_STROKE
                                    }
                                    strokeWidth={isDead || isAmbiguous ? 1.5 : 0.5}
                                    onMouseEnter={() => setHovered(point)}
                                    onMouseLeave={() => setHovered(undefined)}
                                >
                                    <title>{describe(point, azimuth, elevation, isDead, isAmbiguous)}</title>
                                </rect>
                            );
                        }),
                    )}

                    {/* Reported beam centre. Drawn at its interpolated position rather than snapped to a
                        cell, because it is a centroid over the lobe and almost never lands on a grid line. */}
                    {boresight && (
                        <g pointerEvents="none">
                            <circle
                                cx={left + boresight.column * cell + cell / 2}
                                cy={top + boresight.row * cell + cell / 2}
                                r={4}
                                fill="none"
                                stroke="#111827"
                                strokeWidth={1.5}
                            />
                            <line
                                x1={left + boresight.column * cell + cell / 2 - 7}
                                x2={left + boresight.column * cell + cell / 2 + 7}
                                y1={top + boresight.row * cell + cell / 2}
                                y2={top + boresight.row * cell + cell / 2}
                                stroke="#111827"
                                strokeWidth={0.75}
                            />
                            <line
                                x1={left + boresight.column * cell + cell / 2}
                                x2={left + boresight.column * cell + cell / 2}
                                y1={top + boresight.row * cell + cell / 2 - 7}
                                y2={top + boresight.row * cell + cell / 2 + 7}
                                stroke="#111827"
                                strokeWidth={0.75}
                            />
                            <title>
                                Reported beam centre: az {format(grid.boresightAzimuthDegrees)}° el{" "}
                                {format(grid.boresightElevationDegrees)}°
                            </title>
                        </g>
                    )}

                    {/* The window the acceptance checks were applied over. Everything outside it was swept
                        and is drawn, but did not decide whether the unit passed. */}
                    {gradedEdges.map((at, i) => (
                        <line
                            key={`gate-${i}`}
                            x1={left + at * cell - 1}
                            x2={left + at * cell - 1}
                            y1={top - 2}
                            y2={top + grid.elevations.length * cell}
                            stroke="#111827"
                            strokeWidth={0.75}
                            strokeDasharray="3 2"
                            pointerEvents="none"
                        >
                            <title>Graded field of view: ±{grid.evaluationAzimuthLimitDegrees}°</title>
                        </line>
                    ))}

                    <text x={left} y={12} className="fill-gray-400" fontSize={8}>
                        elevation ° (rows) × azimuth ° (columns), commanded
                    </text>
                </svg>
            </div>

            <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                <span>{scale.minLabel}</span>
                <span
                    className="h-2 flex-1 rounded"
                    style={{
                        background: `linear-gradient(to right, ${[0, 0.25, 0.5, 0.75, 1]
                            .map((t) => colourRamp(t))
                            .join(", ")})`,
                    }}
                />
                <span>{scale.maxLabel}</span>
            </div>

            <div className="mt-1 h-8 font-mono text-[10px] leading-tight text-gray-600">
                {hovered
                    ? describe(
                          hovered,
                          hovered.azimuthDegrees,
                          hovered.elevationDegrees,
                          grid.deadCells.has(cellKey(hovered.azimuthDegrees, hovered.elevationDegrees)),
                          grid.ambiguousCells.has(cellKey(hovered.azimuthDegrees, hovered.elevationDegrees)),
                      )
                    : ""}
            </div>
        </div>
    );
};

/** Tooltip text — also the SVG <title>, so it works for keyboard and screen-reader users too. */
const describe = (
    point: FovSweepPoint | undefined,
    azimuth: number,
    elevation: number,
    isDead: boolean,
    isAmbiguous = false,
): string => {
    const where = `az ${azimuth}° el ${elevation}°`;
    if (!point || !point.detected) return `${where} — no detection${isDead ? " (flagged dead)" : ""}`;

    return (
        `${where} — SNR ${format(point.snrDb)} dB, ` +
        `reported az ${format(point.measuredAzimuthDegrees)}° (err ${format(azimuthErrorDegrees(point))}°), ` +
        `reported el ${format(point.measuredElevationDegrees)}°, ` +
        `range ${format(point.measuredRangeMeters, 3)} m, ${point.frameCount} frames` +
        (isDead ? " — flagged dead" : "") +
        (isAmbiguous ? " — flagged ambiguous (seen, but not where it is)" : "")
    );
};

/**
 * The pair of maps shown under a calibration report: how much signal came back at each pointing, and how
 * far the angle the radar reported was from the angle it was pointed at.
 */
export const FovSweepHeatmaps: React.FC<{ grid: FovSweepGrid }> = ({ grid }) => (
    <div>
        <div className="grid gap-4 lg:grid-cols-2">
            <Heatmap
                grid={grid}
                title="Return strength"
                subtitle={`SNR at each commanded angle. Peak ${format(grid.peakSnrDb)} dB; detection threshold ${format(
                    grid.detectionThresholdDb,
                )} dB.`}
                unit="dB"
                value={(point) => (point.detected ? point.snrDb : NaN)}
                ramp={snrRamp}
            />
            <Heatmap
                grid={grid}
                title="Azimuth error"
                subtitle="Reported azimuth minus commanded, absolute. Capped at 45° so ordinary few-degree differences stay readable."
                unit="°"
                value={azimuthErrorDegrees}
                ramp={errorRamp}
                maxValue={45}
            />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-4 text-[10px] text-gray-500">
            <span className="flex items-center gap-1">
                <span
                    className="inline-block h-3 w-3 rounded-sm border"
                    style={{ background: GRID_UNDETECTED_FILL, borderColor: GRID_UNDETECTED_STROKE }}
                />
                no detection
            </span>
            <span className="flex items-center gap-1">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-red-600" />
                flagged as a dead cell
            </span>
            <span className="flex items-center gap-1">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-violet-600" />
                ambiguous — seen, but not where it is
            </span>
            <span className="flex items-center gap-1">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-gray-900" />
                reported beam centre
            </span>
            {grid.evaluationAzimuthLimitDegrees !== undefined && (
                <span className="flex items-center gap-1">
                    <span className="inline-block h-3 w-0 border-l-2 border-dashed border-gray-900" />
                    graded field of view (±{grid.evaluationAzimuthLimitDegrees}°); outside it is trend data
                </span>
            )}
            <span>Hover a cell for its full measurement.</span>
        </div>
    </div>
);
